import { BCMSCurrency, DisabledPrompt, useModel, useRoute } from "@bcm-dev/bcms-vite";
import { Stack } from "@mui/material";
import AccordionView, { AccordionContent, AccordionHeader } from "compid/shared/views/AccordionView";
import BillerView from "compid/shared/views/BillerView";
import BillerPaymentView from "compid/shared/views/BillerView/components/views/BillerPaymentView";
import WidgetView, { WidgetMainContentView } from "compid/shared/views/WidgetView";
import { Payment } from "model";
import React from "react";
import { SVGAsset } from 'res';
import "./styles.css";

/**
 * @function AccordionSection
 * @param {STRING} className - Class name for the container of the accordion.
 * @param {STRING} text - The primary label of the accordion.
 * @param {BOOLEAN} defaultExpanded - Boolean that determines if the accordion is expanded by default.
 * @returns {React.Component}
 * @exported false
 * @description An accordion that toggles payments.
 */
function AccordionSection(props) {
      const className = props.className ?? "";
      const text = props.text ?? "[billerGroupName]";  
      const defaultExpanded = String(props.defaultExpanded ?? true) === "true";
    
      return (
        <AccordionView className={"recent-activity-accordion flex-height " + { className }} defaultExpanded={ defaultExpanded }>
          <AccordionHeader className="biller-groups-accordion-toggle" >
            <Stack direction="row" alignItems="center">
              <SVGAsset id="DownChevronIcon" className="chevron-icon left-icon set-width" />
              <span className="flex-width">{ text }</span>
              <span className="flex-width total-amount-text">{ `Total: ${ props.totalFormatted }` }</span>
            </Stack>
          </AccordionHeader>
          <AccordionContent className="biller-groups-accordion-content flex-container flex-vertical">
            {props.children}
          </AccordionContent>
        </AccordionView>
      );
}

export default function RecentActivityView(props) {
    const model = useModel();
    const navigate = useRoute();
    const onClick = paymentID => evt => navigate(`/viewPayment/${ paymentID }`);
    const hasPayments = model.payments.length > 0;

    // An object we will use to build the activity content.
    // It encapsulates the scheduled, processing, and processed payments.
    const activityObj = model.payments
    .reduce((acc, item) => {
        const payment = Payment.init(item);
        if (payment.date.diff() > -30) acc.push(Payment.init(item));
        return acc;
    }, [])
    .sort((a, b) => {
            if(a.date.isBefore(b.date)) { return 1; }
            else if(b.date.isBefore(a.date)) { return -1; }
            else { return 0; }
        })
    .reduce((acc, item) => {
        switch(item.status) {
            case "scheduled":
                acc.scheduled.total += parseFloat(item.amount.amount);
                acc.scheduled.totalFormatted = BCMSCurrency.init(acc.scheduled.total).format();
                acc.scheduled.items.push(item);
                break;
            case "processing":
                acc.processing.total += parseFloat(item.amount.amount);
                acc.processing.totalFormatted = BCMSCurrency.init(acc.processing.total).format();
                acc.processing.items.push(item);
                break;
            case "processed":
                acc.processed.total += parseFloat(item.amount.amount);
                acc.processed.totalFormatted = BCMSCurrency.init(acc.processed.total).format();
                acc.processed.items.push(item);
                break;
        }
        return acc;
    }, { 
        scheduled: { total: 0, totalFormatted: "", items: [] },
        processing: { total: 0, totalFormatted: "", items: [] },
        processed: { total: 0, totalFormatted: "", items: [] } 
    });
    
    // Returns a biller view.
    const getBillerView = payment => {
        const billerView = <BillerView
        onClick={ onClick(payment.id) }
        // You won't navigate when clicking the biller name link in this view.
        hasBillerNameLinkClick={ false }
        className="recent-activity-view clickable"
        key={ "payment_" + payment.id }
        biller={ payment.to }
        dataPoint={ `Recent_Activity_Payment_${ payment.id }_Biller`  }
        paymentInfo={
            <BillerPaymentView
                amount={ payment.amount }
                status={ payment.status }
                date={ payment.formatDate }
                showAutopay={ payment.hasRelatedAutopay }
                payment={ payment }
                dataPoint={ "Recent_Activity_Payment" }
                key={ payment.id }/>
        }/>;

        return payment.status === "processing"
        ? <React.Fragment key={ "payment_" + payment.id }>
            <DisabledPrompt>{ billerView }</DisabledPrompt>
          </React.Fragment>
        : billerView;
    };

    // Returns an array of biller views.
    const getBillerViews = payments => {
        return payments.reduce((acc, payment) => {
            acc.push(getBillerView(payment));
            return acc;
        }, []);
    };

    // The 3 sections (scheduled, processing, processed) in the activity widget.
    const activity = <>
        <AccordionSection text={ `Scheduled` } 
        totalFormatted={ activityObj.scheduled.totalFormatted } defaultExpanded="true" >
            {
                getBillerViews(activityObj.scheduled.items)
            }
        </AccordionSection>
        <AccordionSection text={ `Processing` }
        totalFormatted={ activityObj.processing.totalFormatted } defaultExpanded="true">
            {
                getBillerViews(activityObj.processing.items)
            }
        </AccordionSection>
        <AccordionSection text={ `Past payments` }
        totalFormatted={ activityObj.processed.totalFormatted } defaultExpanded="false">
            {
                getBillerViews(activityObj.processed.items)
            }
        </AccordionSection>
    </>;

    // Return the 3 sections.
    return hasPayments ? activity : <NoActivity biller={ props.biller }/>;
}

/**
 * @function NoActivity
 * @returns {React.Component}
 * @exported false
 * @description A component shown if there are no payments.
 */
function NoActivity(props) {
    return  <WidgetView className="empty">
                <WidgetMainContentView>
                <span className="sub-text">You have no recent payments.</span>
                </WidgetMainContentView>
            </WidgetView>
}  